<template>
  <div class="stripe-payment-element">
    <div v-if="wait.is('Loading')" class="wait-overflow">
      <loading-spinner />
    </div>
    <!-- Ignore the unused `instance` variable.
      It is marked as "// attention: important part!" in vue-stripe-js docs
      -->
    <!-- eslint-disable  vue/no-unused-vars -->
    <StripeElements
      ref="stripeElementsRef"
      :key="`${remountStripeElements16218}`"
      :stripe-key="STRIPE_PUBLIC_KEY"
      :instance-options="instanceOptions"
      :elements-options="elementsOptions"
    >
      <StripeElement
        id="express-checkout-element"
        ref="expressCheckoutRef"
        :type="'expressCheckout'"
        :options="expressCheckoutOptions"
        @change="changedHandler"
        @click="expressCheckoutClick"
      />
      <StripeElement
        ref="paymentElementRef"
        :type="'payment'"
        :options="paymentElementOptions"
        @change="changedHandler"
      />
    </StripeElements>
    <!-- eslint-enable  vue/no-unused-vars -->
  </div>
  <FormError
    :class="validation.stateClass('card') === 'is-invalid' ? 'd-block' : ''"
    :errors="validation.errors"
    field="card"
  />
  <button
    v-if="showButton"
    class="btn btn-primary mt-3"
    :disabled="!complete || saving"
    @click="save"
  >
    Save Card
  </button>
  <div v-show="savedAlert" class="alert alert-success">Card Saved</div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, watch, onBeforeUnmount, ref } from 'vue'
import { useRoute } from 'vue-router'

// The vue-stripe-js package doesn't have typing
// @ts-ignore
import { StripeElements, StripeElement } from 'vue-stripe-js'
import type {
  StripeElementsOptionsMode,
  StripePaymentElementOptions,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js'

import { STRIPE_PUBLIC_KEY } from '@/init/settings'
import { ValidationState } from '@/helpers/form'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'

import { BillingView } from '@/models/billing'
import FormError from '@/components/ui/FormError.vue'

import { getEmail } from '@/services/auth'
import { useWait } from '@/app/composables/useWait'
import { emitter } from '@/services/mitt'

const props = withDefaults(
  defineProps<{
    billing: BillingView
    showButton?: boolean
  }>(),
  {
    showButton: true,
  },
)

const emit = defineEmits(['saved' as string])
const wait = useWait()
const route = useRoute()

const stripeElementsRef = ref<typeof StripeElements | null>(null)
const expressCheckoutRef = ref<typeof StripeElement | null>(null)
const paymentElementRef = ref<typeof StripeElement | null>(null)

const validation = ref(new ValidationState())

// True once card data has been changed
const changed = ref(false)

// True once Stripe reports the the card is valid (but we din't save it yet).
const complete = ref(false)
// True while we are saving card data.
const saving = ref(false)
// True once we successfully saved card, triggers the success alert.
const saved = ref(false)

// This value is used as the `key` to the `StripeElements` component,
// so we can toggle it to remount the component.
const remountStripeElements16218 = ref(false)

const setupIntent = ref<Record<string, any>>({})
// True once we confirmed the setupIntent
const setupIntentConfirmed = ref(false)

// https://stripe.com/docs/js/initializing#init_stripe_js-options
const instanceOptions = ref<Record<string, any>>({})
// https://stripe.com/docs/stripe.js#element-options
const paymentElementOptions = ref<StripePaymentElementOptions>({
  layout: {
    type: 'tabs',
    defaultCollapsed: false,
  },
})

// https://stripe.com/docs/js/elements_object/create#stripe_elements-options
const elementsOptions = ref<StripeElementsOptionsMode>({
  mode: 'setup',
  currency: 'usd',
  appearance: {
    // To style the Payment Element we use Stripe Appearance API
    // https://docs.stripe.com/elements/appearance-api
    theme: 'stripe',
    variables: {
      colorPrimaryText: '#141C31',
      colorBackground: '#F8F8F8',
      fontFamily: 'Rotunda, sans-serif',
      fontSizeBase: '16px',
    },
    rules: {
      '.Input': {
        border: '1px solid #D5D8E0',
        lineHeight: '20px',
      },
      '.Label': {
        fontWeight: '500',
        lineHeight: '18px',
      },
      '.Tab': {
        border: '1px solid #D4D0D0',
      },
      '.Tab--selected': {
        border: '1px solid #363B48',
      },
    },
  },
})

const expressCheckoutOptions = ref<StripeExpressCheckoutElementOptions>({
  buttonType: {
    applePay: 'subscribe',
    googlePay: 'subscribe',
  },
  buttonTheme: {
    applePay: 'white',
    googlePay: 'white',
  },
  paymentMethods: {
    applePay: 'always',
    googlePay: 'always',
  },
  paymentMethodOrder: ['apple_pay', 'google_pay', 'link'],
})

onBeforeMount(async () => {
  if (route.name === 'billing_apple_external') {
    emitter.on('stripeFinishedLoading16218', remountWhenStripeLoads16218)
  }

  // Pre-fill user's email for the Stripe Link payment
  const userEmail = getEmail()
  if (userEmail) {
    paymentElementOptions.value.defaultValues = {
      billingDetails: {
        email: userEmail,
      },
    }
  } else {
    Sentry.captureMessage('User email is null in the subscribe popup')
  }
  setupIntent.value = await backend.getSetupIntent()
})

watch(
  // @ts-ignore
  () => expressCheckoutRef.value?.stripeElement,
  (element) => {
    if (!element) {
      return
    }

    const handleReady = (): void => {
      element.addEventListener('confirm', expressCheckoutConfirm)
    }

    // When Stripe Element `on()` function becomes available, attach handler
    if (typeof element.on === 'function') {
      element.on('ready', handleReady)
    } else {
      // Fallback: bind immediately if already initialized (past the `ready` state)
      handleReady()
    }
  },
  { immediate: true },
)

onBeforeUnmount(async () => {
  // If the user didn't add payment data, cancel the setup intent
  if (!setupIntentConfirmed.value) {
    await backend.cancelSetupIntent(setupIntent.value)
  }
  emitter.off('stripeFinishedLoading16218', remountWhenStripeLoads16218)
})

function changedHandler($event: any): void {
  changed.value = true
  complete.value = $event.complete
}

function needsSaving(): boolean {
  return changed.value
}

async function paymentElementConfirmStripe(): Promise<any> {
  // @ts-ignore
  const confirmSetupFn = stripeElementsRef.value.instance.confirmSetup
  const params = {
    // @ts-ignore
    elements: stripeElementsRef.value.elements,
    clientSecret: setupIntent.value.client_secret,
    redirect: 'if_required',
    confirmParams: {
      return_url: 'https://www.shortform.com/app/billing',
    },
  }
  // @ts-ignore
  await stripeElementsRef.value.elements.submit()
  const { error } = await confirmSetupFn(params)
  if (error) {
    return { error: error }
  }

  setupIntentConfirmed.value = true
  return { success: true }
}

/**
 * Express Checkout Element click handler.
 *
 * We need to have the click handler for the element,
 * because we need to immediately call `event.resolve()` with `client_secret`.
 */
async function expressCheckoutClick(event: Event): Promise<any> {
  // @ts-ignore
  event.resolve(setupIntent.value.clientSecret)
}

/**
 * Express Checkout Element confirm handler.
 *
 * This method is called after the user goes through the payment option's
 * process: for example, chooses card, clicks confirm.
 */
async function expressCheckoutConfirm(): Promise<any> {
  // @ts-ignore
  const { error: submitError } = await stripeElementsRef.value.elements.submit()
  if (submitError) {
    handleStripeError(submitError)
    return
  }

  // Create a ConfirmationToken using the details collected by the Express Checkout Element
  // @ts-ignore
  const { error, confirmationToken } =
    // @ts-ignore
    await stripeElementsRef.value.instance.createConfirmationToken({
      // @ts-ignore
      elements: stripeElementsRef.value.elements,
      params: {
        return_url: 'https://www.shortform.com/app/billing',
      },
    })

  if (error) {
    // From Stripe documentation:
    // This point is only reached if there's an immediate error when
    // confirming the payment. Show the error to your customer (for example, payment details incomplete)
    // https://docs.stripe.com/elements/express-checkout-element/accept-a-payment?client=html#create-ct
    handleStripeError(error)
    return false
  }

  // @ts-ignore
  const confirmSetupFn = stripeElementsRef.value.instance.confirmSetup
  const params = {
    clientSecret: setupIntent.value.client_secret,
    redirect: 'if_required',
    confirmParams: {
      confirmation_token: confirmationToken.id,
      return_url: 'https://www.shortform.com/app/billing',
    },
  }
  // @ts-ignore
  const { confirmationError } = await confirmSetupFn(params)
  if (confirmationError) {
    handleStripeError(confirmationError)
    return false
  }
  setupIntentConfirmed.value = true
  wait.start('Loading')
  const billing = await backend.savePaymentMethod({
    id: setupIntent.value.id,
  })
  wait.end('Loading')
  props.billing.card_data = billing.card_data
  _setSaved('')

  // There is a new billing card data returned by stripe,
  // if we saved it successfully, update props.billing,
  // so the new card is displayed.
  emit('saved')

  return true
}

/**
 * Save card, returns true on success
 */
async function save(): Promise<boolean> {
  saving.value = true

  try {
    const data = await paymentElementConfirmStripe()
    if (data.error) {
      handleStripeError(data.error)
      return false
    }
    const billing = await backend.savePaymentMethod({
      id: setupIntent.value.id,
    })
    props.billing.card_data = billing.card_data
    _setSaved('')

    // There is a new billing card data returned by stripe,
    // if we saved it successfully, update props.billing,
    // so the new card is displayed.
    emit('saved')
    return true
  } catch (error) {
    _setSaved(error)
  }
  return false
}

/**
 * Update state flags depending on the error value.
 */
function _setSaved(error: any): void {
  if (error) {
    saving.value = false
    saved.value = false
    changed.value = true
    validation.value.showErrors(error)
  } else {
    saving.value = false
    saved.value = true
    changed.value = false
    validation.value.reset()
  }
}

function handleStripeError(error: any): void {
  // Something went wrong on Stripe side.
  const expected_errors = [
    'incomplete_zip',
    'incomplete_cvc',
    'incomplete_expiry',
    'incomplete_number',
    'invalid_number',
    'invalid_expiry_month_past',
  ]
  if (error.code && expected_errors.indexOf(error.code) === -1) {
    // Unexpected error, send to Sentry.
    Sentry.withScope((scope) => {
      scope.setExtra('stripeData', error)
      Sentry.captureMessage(error.message)
    })
  }
  _setSaved({
    response: {
      status: 400,
      data: { errors: { card: [error.message] } },
    },
  })
}

const savedAlert = computed(() => {
  if (saved.value) {
    // 3 seconds to hide the alert
    return 3
  }
  return false
})

/**
 * Remount the component when Stripe is loaded in the apple external billing page.
 *
 * In the external billing page, this component can be mounted before Stripe is initialized
 * (in `@/init/stripe-init.ts`), which makes the payment form fail to load.
 */
function remountWhenStripeLoads16218(): void {
  if (!stripeElementsRef.value?.elements) {
    remountStripeElements16218.value = true
  }
}

defineExpose({
  needsSaving,
  save,
})
</script>

<style lang="scss" scoped>
.stripe-payment-element {
  min-height: 345px;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  width: 100%;

  :deep(.StripeElement) {
    height: 100% !important;
    margin-bottom: 20px;
    padding: 0;
  }
}

.wait-overflow {
  display: flex;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba($c-white, 0.85);
  z-index: 3;
  border-radius: 7px;
  align-items: center;
  justify-content: center;
}

:deep(.StripeElement) {
  box-sizing: border-box;
  height: 60px;
  padding: 20px 12px;
  border-radius: 8px;
}

:deep(.StripeElement--focus) {
  box-shadow: 0 1px 3px 0 #cfd7df;
}

:deep(.StripeElement--invalid) {
  border-color: #fa755a;
}

:deep(.StripeElement--webkit-autofill) {
  background-color: #fefde5 !important;
}

:deep(.loading__spinner) {
  margin: 0;
}
</style>
